import { createContextAndHook } from '@clerk/shared/react';
import React, { useCallback } from 'react';

import type { LocalizationKey } from '../customizables';
import { Button, Col, descriptors, Flex, Heading, Icon, Span, Text, useLocalizations } from '../customizables';
import type { ElementDescriptor, ElementId } from '../customizables/elementDescriptors';
import { useNavigateToFlowStart, usePopover } from '../hooks';
import { Menu } from '../icons';
import { useRouter } from '../router';
import type { PropsOfComponent, ThemableCssProp } from '../styledSystem';
import { animations, common, mqu } from '../styledSystem';
import { colors } from '../utils/colors';
import { Card } from './Card';
import { withFloatingTree } from './contexts';
import { DevModeOverlay } from './DevModeNotice';
import { Popover } from './Popover';

type NavbarContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  contentRef?: React.RefObject<HTMLDivElement>;
};
export const [NavbarContext, useNavbarContext, useUnsafeNavbarContext] =
  createContextAndHook<NavbarContextValue>('NavbarContext');

export const NavbarContextProvider = ({
  children,
  contentRef,
}: React.PropsWithChildren<{ contentRef?: React.RefObject<HTMLDivElement> }>) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);
  const value = React.useMemo(() => ({ value: { isOpen, open, close, contentRef } }), [isOpen]);
  return <NavbarContext.Provider value={value}>{children}</NavbarContext.Provider>;
};

export type NavbarRoute = {
  name: LocalizationKey | string;
  id: string;
  icon: React.ComponentType;
  path: string;
  external?: boolean;
};
type NavBarProps = {
  title: LocalizationKey;
  titleSx?: ThemableCssProp;
  containerSx?: ThemableCssProp;
  description?: LocalizationKey;
  contentRef: React.RefObject<HTMLDivElement>;
  routes: NavbarRoute[];
  header?: React.ReactNode;
};

export const NavBar = (props: NavBarProps) => {
  const { contentRef, title, titleSx, containerSx, description, routes, header } = props;
  const { close } = useNavbarContext();
  const { navigate } = useRouter();
  const { navigateToFlowStart } = useNavigateToFlowStart();
  const router = useRouter();

  const handleNavigate = (route: NavbarRoute) => {
    if (route?.external) {
      return () => navigate(route.path);
    } else {
      return () => navigateToInternalRoute(route);
    }
  };

  const navigateToInternalRoute = async (route: NavbarRoute) => {
    if (contentRef.current) {
      close();
      if (route.path === '/') {
        // TODO: this is needed to correctly handle navigations
        // when the component is opened as a modal
        await navigateToFlowStart();
      } else {
        await navigate(route.path);
      }
    }
  };

  const checkIfActive = useCallback(
    (a: NavbarRoute) => {
      if (a.external) {
        return false;
      }
      const isRoot = router.currentPath === router.fullPath && a.path === '/';
      const matchesPath = router.matches(a.path);
      return isRoot || matchesPath;
    },
    [router.currentPath],
  );

  const items =
    routes.length > 0 ? (
      <Col
        elementDescriptor={descriptors.navbarButtons}
        sx={t => ({
          gap: t.space.$0x5,
        })}
      >
        {routes.map(r => (
          <NavButton
            key={r.id}
            elementDescriptor={descriptors.navbarButton}
            elementId={descriptors.navbarButton.setId(r.id as any)}
            iconElementDescriptor={descriptors.navbarButtonIcon}
            iconElementId={descriptors.navbarButtonIcon.setId(r.id) as any}
            onClick={handleNavigate(r)}
            icon={r.icon}
            isActive={checkIfActive(r)}
            sx={t => ({
              padding: `${t.space.$1x5} ${t.space.$3}`,
              minHeight: t.space.$8,
            })}
          >
            <Span
              elementDescriptor={descriptors.navbarButtonText}
              elementId={descriptors.navbarButtonText.setId(r.id as any)}
              localizationKey={r.name}
            />
          </NavButton>
        ))}
      </Col>
    ) : null;

  return (
    <>
      <NavbarContainer
        title={title}
        titleSx={titleSx}
        containerSx={containerSx}
        description={description}
      >
        {header}
        {items}
      </NavbarContainer>
      <MobileNavbarContainer>
        {header}
        {items}
      </MobileNavbarContainer>
    </>
  );
};

const NavbarContainer = (
  props: React.PropsWithChildren<{
    title: LocalizationKey | string;
    titleSx?: ThemableCssProp;
    containerSx?: ThemableCssProp;
    description?: LocalizationKey | string;
  }>,
) => {
  const { title, titleSx, containerSx, description } = props;
  return (
    <Col
      elementDescriptor={descriptors.navbar}
      sx={t => ({
        [mqu.md]: {
          display: 'none',
        },
        flex: `0 0 ${t.space.$57}`,
        width: t.sizes.$57,
        position: 'relative',
        maxWidth: t.space.$57,
        background: common.mutedBackground(t),
        padding: `${t.space.$6} ${t.space.$5} ${t.space.$4} ${t.space.$3}`,
        marginInlineEnd: `calc(${t.space.$2} * -1)`,
        color: t.colors.$colorForeground,
        justifyContent: 'space-between',
      })}
    >
      <DevModeOverlay />

      <Col sx={[t => ({ gap: t.space.$6, flex: `0 0 ${t.space.$60}` }), containerSx]}>
        <Col
          sx={t => ({
            gap: t.space.$0x5,
            padding: `${t.space.$none} ${t.space.$3}`,
          })}
        >
          <Heading
            as='h1'
            sx={titleSx}
            localizationKey={title}
          />

          {description ? (
            <Text
              colorScheme='secondary'
              localizationKey={description}
            />
          ) : null}
        </Col>
        {props.children}
      </Col>

      <Card.ClerkAndPagesTag
        sx={{
          width: 'fit-content',
        }}
        devModeNoticeSx={t => ({
          // Match the card footer so the notice sits the same distance from the bottom as sign-in/sign-up.
          padding: t.space.$none,
          marginBottom: `calc(${t.space.$1} * -1)`,
        })}
      />
    </Col>
  );
};

const MobileNavbarContainer = withFloatingTree((props: React.PropsWithChildren<Record<never, never>>) => {
  const navbarContext = useNavbarContext();
  const { floating, isOpen, open, close, nodeId, context } = usePopover({ defaultOpen: false, autoUpdate: false });

  React.useEffect(() => {
    if (navbarContext.isOpen) {
      open();
    } else {
      close();
    }
  }, [navbarContext.isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      navbarContext.close();
    }
  }, [isOpen]);

  return (
    <Popover
      nodeId={nodeId}
      context={context}
      isOpen={isOpen}
      portal={false}
    >
      <Col
        sx={t => ({
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '100%',
          zIndex: t.zIndices.$navbar,
          overflow: 'hidden',
          color: t.colors.$colorForeground,
        })}
      >
        <Col
          ref={floating}
          elementDescriptor={descriptors.navbar}
          tabIndex={0}
          sx={t => ({
            outline: 0,
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: t.space.$60,
            backgroundColor: colors.makeSolid(t.colors.$colorBackground),
            borderStartEndRadius: t.radii.$lg,
            borderEndEndRadius: t.radii.$lg,
            padding: `${t.space.$10} ${t.space.$6}`,
            animation: `${animations.navbarSlideIn} ${t.transitionDuration.$slower} ${t.transitionTiming.$slowBezier}`,
            borderInlineEndWidth: t.borderWidths.$normal,
            borderInlineEndStyle: t.borderStyles.$solid,
            borderInlineEndColor: t.colors.$borderAlpha150,
            boxShadow: t.shadows.$cardContentShadow,
          })}
        >
          {props.children}
        </Col>
      </Col>
    </Popover>
  );
});

type NavButtonProps = PropsOfComponent<typeof Button> & {
  icon: React.ComponentType;
  iconElementDescriptor: ElementDescriptor;
  iconElementId: ElementId;
  isActive?: boolean;
};

const NavButton = (props: NavButtonProps) => {
  const { icon, children, isActive, iconElementDescriptor, iconElementId, sx, ...rest } = props;
  return (
    <Button
      variant='unstyled'
      colorScheme={isActive ? 'primary' : 'neutral'}
      textVariant='buttonLarge'
      size='md'
      isActive={isActive}
      focusRing={false}
      {...rest}
      sx={[
        t => ({
          gap: t.space.$3,
          justifyContent: 'flex-start',
          backgroundColor: isActive ? t.colors.$neutralAlpha100 : undefined,
          color: isActive ? t.colors.$primary500 : t.colors.$colorMutedForeground,
          '&:hover': {
            backgroundColor: isActive ? undefined : t.colors.$neutralAlpha25,
          },
          '&:focus-visible': {
            backgroundColor: isActive ? undefined : t.colors.$neutralAlpha50,
          },
        }),
        sx,
      ]}
    >
      <Icon
        elementDescriptor={iconElementDescriptor}
        elementId={iconElementId}
        icon={icon}
        sx={{
          opacity: isActive ? 1 : 0.7,
          // Fixes a bug in Safari where the icon shifts when navigating between routes.
          transform: 'translateZ(0)',
        }}
      />
      {children}
    </Button>
  );
};

type NavbarMenuButtonRowProps = PropsOfComponent<typeof Button> & {
  navbarTitleLocalizationKey?: LocalizationKey;
};

export const NavbarMenuButtonRow = ({ navbarTitleLocalizationKey, ...props }: NavbarMenuButtonRowProps) => {
  const { open } = useUnsafeNavbarContext();
  const { t } = useLocalizations();

  const navbarContextExistsInTree = !!open;
  if (!navbarContextExistsInTree) {
    return null;
  }

  return (
    <Flex
      elementDescriptor={descriptors.navbarMobileMenuRow}
      sx={t => ({
        display: 'none',
        background: common.mutedBackground(t),
        padding: `${t.space.$2} ${t.space.$3} ${t.space.$4} ${t.space.$3}`,
        marginBottom: `calc(${t.space.$2} * -1)`,
        [mqu.md]: {
          display: 'flex',
        },
      })}
    >
      <Button
        elementDescriptor={descriptors.navbarMobileMenuButton}
        {...props}
        onClick={open}
        size='xs'
        textVariant='h2'
        variant='ghost'
        sx={t => ({
          color: t.colors.$colorForeground,
          gap: t.space.$2x5,
          width: 'fit-content',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <Icon
          elementDescriptor={descriptors.navbarMobileMenuButtonIcon}
          icon={Menu}
          size='md'
        />
        {t(navbarTitleLocalizationKey)}
      </Button>
    </Flex>
  );
};
